import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { Course } from '@/client/courses';
import { sortAlphabetically } from '@/utils/helpers';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { SelectItemOptionsType } from 'primereact/selectitem';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDropdown = styled(Dropdown)`
  border-radius: var(--default-border-radius);
  background: var(--white-main);
  width: 450px;
  max-width: 100%;

  .p-dropdown-label.p-placeholder,
  .p-dropdown-label.p-inputtext {
    background: var(--white-main);
  }

  .p-dropdown-label.p-inputtext {
    padding-right: 0px;
  }
`;

// Only support English and Danish for customization
const SUPPORTED_CUSTOMIZATION_LANGUAGES = [LanguagesEnum.EN, LanguagesEnum.DK];

type CourseCustomizationLanguageSelectorProps = {
  course: Course;
  onChange: (value: LanguagesEnum) => void;
  selectedLanguage?: LanguagesEnum;
  isDisabled?: boolean;
  className?: string;
};

export const CourseCustomizationLanguageSelector: React.FC<
  CourseCustomizationLanguageSelectorProps
> = ({ course, onChange, selectedLanguage, isDisabled, className }) => {
  const { t } = useTranslation();
  const [availableLanguages, setAvailableLanguages] =
    useState<SelectItemOptionsType>([]);

  useEffect(() => {
    // Filter course languages to only include supported customization languages
    const supportedCourseLanguages = course.courseLanguages.filter((language) =>
      SUPPORTED_CUSTOMIZATION_LANGUAGES.includes(language),
    );

    // Create dropdown options
    const languageOptions = supportedCourseLanguages.map((language) => ({
      id: language,
      label: languageDisplayLabel[language],
    }));

    // Sort alphabetically
    const sortedLanguageOptions = languageOptions.sort((a, b) =>
      sortAlphabetically(a.label, b.label),
    );

    setAvailableLanguages(sortedLanguageOptions);
  }, [course]);

  const onChangeHandler = (e: DropdownChangeEvent) => {
    onChange(e.value);
  };

  // If no languages are available, don't render the dropdown
  if (availableLanguages.length === 0) {
    return null;
  }

  return (
    <label>
      <StyledDropdown
        id="customization-language"
        placeholder={t('generic.select')}
        value={selectedLanguage}
        optionValue="id"
        optionLabel="label"
        onChange={onChangeHandler}
        options={availableLanguages}
        className={className}
        disabled={isDisabled}
        filter
        {...preventDropdownBrowserTranslate}
      />
    </label>
  );
};
